import type { ReactNode } from 'react'
import {
  Box,
  Checkbox,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
} from '@mui/material'
import { useUiStore, type Density } from '../stores/uiStore.js'

/**
 * Tableau de données unique de l'application (§8.5).
 *
 * Une seule implémentation pour TOUTES les listes : clients, articles,
 * transactions… Sans cela, chaque écran réinvente son tri, sa pagination et
 * ses états vides, et ils divergent au fil des sprints.
 */

export interface Column<T> {
  key: string
  label: string
  /** Clé de tri envoyée à l'API ; absente = colonne non triable. */
  sortKey?: string
  align?: 'left' | 'right'
  /** Chiffres tabulaires : indispensable pour aligner montants et quantités. */
  numeric?: boolean
  width?: number | string
  render: (row: T) => ReactNode
}

const ROW_HEIGHT: Record<Density, number> = {
  compact: 32,
  standard: 40,
  comfortable: 52,
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  loading?: boolean
  total?: number
  limit?: number
  offset?: number
  sort?: string
  onSortChange?: (sort: string) => void
  onPageChange?: (offset: number, limit: number) => void
  onRowClick?: (row: T) => void
  /**
   * Style propre a une ligne, selon son etat.
   *
   * ⭐ Sert a signaler une anomalie SUR LA LIGNE et non dans une seule cellule :
   * une valeur en rouge perdue au milieu d'un tableau de vingt lignes se
   * remarque a peine, alors qu'un liseré coloré se voit en balayant la page.
   */
  rowSx?: (row: T) => object | undefined
  /**
   * Sélection de lignes par cases à cocher.
   *
   * ⭐ Une case plutôt qu'une corbeille par ligne : traiter dix articles
   * demandait dix clics et dix confirmations. La sélection rassemble l'action
   * en haut, où sont déjà les autres commandes, et une seule confirmation
   * annonce ce qui va vraiment disparaître.
   *
   * L'action elle-même et sa CONFIRMATION restent à la charge de l'appelant :
   * lui seul sait ce que la suppression emporte.
   */
  selected?: string[]
  onSelectionChange?: (ids: string[]) => void
  emptyState?: ReactNode
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  total = 0,
  limit = 50,
  offset = 0,
  sort,
  onSortChange,
  onPageChange,
  onRowClick,
  rowSx,
  selected,
  onSelectionChange,
  emptyState,
}: DataTableProps<T>): JSX.Element {
  const selectionActive = Boolean(onSelectionChange)
  const choisis = new Set(selected ?? [])
  const density = useUiStore((s) => s.density)
  const height = ROW_HEIGHT[density]
  /**
   * Largeur d'une colonne.
   *
   * ⭐ Les colonnes de CHIFFRES se resserrent sur leur contenu ; celles de
   * texte se partagent le reste. C'est ce qui supprime les trous sans laisser
   * de blanc en bout de ligne.
   *
   * ⚠️ Le défaut venait de là : le navigateur répartit l'espace libre entre
   * TOUTES les colonnes au prorata de leur contenu. Une colonne de montants
   * est alignée à droite — elle s'élargissait donc en poussant son contenu
   * vers la droite, et le vide apparaissait AVANT elle. On a vu plusieurs
   * centimètres entre « Dernière activité » et « Montant des achats ».
   *
   * ⚠️ Faire absorber tout le reste à une seule colonne a été essayé : le
   * trou changeait de place, sans disparaître. Le partager entre les colonnes
   * de texte, dont le contenu est naturellement variable, le dissout.
   *
   * ⚠️ `1px` sur une cellule de tableau ne fait pas un pixel : c'est une
   * largeur MINIMALE que le contenu élargit. C'est la façon courante de dire
   * « au plus juste ».
   */
  const largeur = (column: Column<T>): number | string | undefined =>
    column.width ?? (column.numeric ? '1px' : undefined)

  // Le tri s'exprime « champ » ou « -champ » : une seule chaîne, donc une seule
  // valeur à mettre dans l'URL et à renvoyer à l'API.
  const currentField = sort?.replace(/^-/, '')
  const currentDir: 'asc' | 'desc' = sort?.startsWith('-') ? 'desc' : 'asc'

  function toggleSort(column: Column<T>): void {
    if (!column.sortKey || !onSortChange) return
    const isCurrent = currentField === column.sortKey
    onSortChange(isCurrent && currentDir === 'asc' ? `-${column.sortKey}` : column.sortKey)
  }

  if (!loading && rows.length === 0) {
    return <Paper sx={{ p: 12, borderRadius: 3, textAlign: 'center' }}>{emptyState}</Paper>
  }

  return (
    <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
      {/* Le tableau défile dans SON conteneur : la page ne défile jamais
          horizontalement, y compris sur mobile. */}
      <TableContainer sx={{ overflowX: 'auto' }}>
        {/* ⚠️ `width: auto` avec un plancher à 100 %, et c'est ce couple qui
            supprime les trous.
            
            MUI pose `width: 100%` : le navigateur répartit alors l'espace
            libre entre les colonnes, et un tableau dense se troue à l'endroit
            le moins prévisible — ici entre « Dernière activité » et
            « Montant des achats ». En largeur automatique, il n'y a plus
            d'espace à répartir : chaque colonne prend ce qu'il lui faut.
            
            Le plancher garde le cas inverse : un tableau de trois colonnes ne
            doit pas flotter au milieu de sa carte. */}
        <Table
          size={density === 'comfortable' ? 'medium' : 'small'}
          sx={{ width: 'auto', minWidth: '100%' }}
        >
          <TableHead>
            <TableRow>
              {selectionActive && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    // Indéterminée quand la sélection est partielle : l'état
                    // « certains » ne doit pas se confondre avec « aucun ».
                    indeterminate={choisis.size > 0 && choisis.size < rows.length}
                    checked={rows.length > 0 && choisis.size === rows.length}
                    onChange={(e) =>
                      onSelectionChange?.(e.target.checked ? rows.map((r) => rowKey(r)) : [])
                    }
                    inputProps={{ 'aria-label': 'Tout sélectionner' }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align ?? 'left'}
                  sx={{
                    width: largeur(column),
                    bgcolor: 'background.default',
                    fontWeight: 600,
                    /**
                     * ⚠️ L'entête peut passer À LA LIGNE, le contenu non.
                     *
                     * Un libellé comme « Montant des achats » est plus long
                     * que ses valeurs : c'est LUI qui imposait la largeur de
                     * la colonne. Sur la liste des clients, les entêtes à eux
                     * seuls faisaient déborder le tableau de 65 px et la
                     * dernière colonne sortait de l'écran. Deux lignes
                     * d'entête coûtent quelques pixels de hauteur, une fois,
                     * là où la largeur manque à chaque ligne.
                     */
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                  }}
                >
                  {column.sortKey && onSortChange ? (
                    <TableSortLabel
                      active={currentField === column.sortKey}
                      direction={currentField === column.sortKey ? currentDir : 'asc'}
                      onClick={() => toggleSort(column)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading
              ? // Squelette et non roue : la mise en page ne saute pas quand
                // les données arrivent.
                Array.from({ length: 8 }, (_, i) => (
                  <TableRow key={`skeleton-${i}`} sx={{ height }}>
                    {selectionActive && <TableCell padding="checkbox" />}
                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        <Skeleton variant="text" width="70%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow
                    key={rowKey(row)}
                    hover={Boolean(onRowClick)}
                    onClick={() => onRowClick?.(row)}
                    tabIndex={onRowClick ? 0 : -1}
                    onKeyDown={(e) => {
                      if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        onRowClick(row)
                      }
                    }}
                    sx={{
                      height,
                      cursor: onRowClick ? 'pointer' : 'default',
                      ...(rowSx?.(row) ?? {}),
                    }}
                  >
                    {selectionActive && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={choisis.has(rowKey(row))}
                          // ⚠️ Stoppe la propagation : cocher ne doit pas
                          // ouvrir la fiche derrière.
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const suite = new Set(choisis)
                            if (e.target.checked) suite.add(rowKey(row))
                            else suite.delete(rowKey(row))
                            onSelectionChange?.([...suite])
                          }}
                          inputProps={{ 'aria-label': 'Sélectionner cette ligne' }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align ?? 'left'}
                        className={column.numeric ? 'tabular' : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          width: largeur(column),
                        }}
                      >
                        {column.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {onPageChange && (
        <TablePagination
          component="div"
          count={total}
          page={Math.floor(offset / limit)}
          rowsPerPage={limit}
          rowsPerPageOptions={[25, 50, 100, 200]}
          onPageChange={(_, page) => onPageChange(page * limit, limit)}
          onRowsPerPageChange={(e) => onPageChange(0, Number(e.target.value))}
          labelRowsPerPage="Par page"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
        />
      )}
    </Paper>
  )
}

/** État vide réutilisable (§W-38) : illustration, message, action. */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}): JSX.Element {
  return (
    <Box>
      {icon && <Box sx={{ color: 'text.disabled', mb: 3 }}>{icon}</Box>}
      <Typography variant="h3" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5 }}>
          {description}
        </Typography>
      )}
      {action}
    </Box>
  )
}
